import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import Navbar from "../navbar/navbar";
import Footer from "../footer/footer";
import Background from "../Background/Background";
import {
  fadeInUp, fadeInLeft, fadeInRight,
  staggerContainer, scaleIn, slideUp
} from "../../animations/animations";

// Icons
import {
  FiMonitor, FiGlobe, FiTerminal, FiMessageSquare,
  FiDownload, FiArrowRight, FiCode, FiStar, FiZap, FiTool, FiBookOpen
} from "react-icons/fi";
import { FaGithub, FaLinkedin } from "react-icons/fa";
import {
  SiElectron, SiWebrtc, SiNodedotjs, SiReact,
  SiSocketdotio, SiExpress, SiUbuntu
} from "react-icons/si";

// Assets
import desktopappImage from "../../assets/img/desktopapp.png";
import desktopImage from "../../assets/img/desktop.png";
import windowsFile from "../../assets/desktop-app/windows.7z";
import ubuntuFile from "../../assets/desktop-app/linux.7z";
import "./home.css";

// ─── Typing Hook (FIXED) ──────────────────────────────────────
function useTypingEffect(words, speed = 90, deleteSpeed = 50, pause = 2000) {
  const [text, setText] = useState("");
  const [wordIndex, setWordIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const pauseTimer = useRef(null);

  // Cleanup pause timer on unmount
  useEffect(() => {
    return () => {
      if (pauseTimer.current) clearTimeout(pauseTimer.current);
    };
  }, []);

  useEffect(() => {
    const current = words[wordIndex % words.length];

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing forward
        const next = current.slice(0, text.length + 1);
        setText(next);
        if (next === current) {
          // Finished typing — pause then start deleting
          pauseTimer.current = setTimeout(() => setIsDeleting(true), pause);
        }
      } else {
        // Deleting
        const next = current.slice(0, text.length - 1);
        setText(next);
        if (next === "") {
          // Finished deleting — move to next word
          setIsDeleting(false);
          setWordIndex((i) => (i + 1) % words.length);
        }
      }
    }, isDeleting ? deleteSpeed : speed);

    return () => clearTimeout(timeout);
  }, [text, isDeleting, wordIndex, words, speed, deleteSpeed, pause]);

  return text;
}

// ─── Animated Counter ─────────────────────────────────────────
function AnimatedCounter({ target, duration = 2000 }) {
  const [count, setCount] = useState(0);
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);

  return <span ref={ref}>{count}</span>;
}

// ─── Project Card ─────────────────────────────────────────────
function ProjectCard({ icon: Icon, title, description, tags, color, onClick, delay = 0 }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      className="project-card"
      variants={slideUp}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
      whileHover={{
        y: -10,
        boxShadow: `0 25px 60px rgba(${color}, 0.25)`,
        borderColor: `rgba(${color}, 0.5)`,
      }}
      onClick={onClick}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      {/* Gradient border top */}
      <div className="card-top-border" style={{ background: `linear-gradient(90deg, rgba(${color},0.8), transparent)` }} />

      {/* Icon */}
      <div className="card-icon" style={{ background: `rgba(${color}, 0.12)`, color: `rgb(${color})` }}>
        <Icon size={22} />
      </div>

      {/* Content */}
      <h3 className="card-title">{title}</h3>
      <p className="card-desc">{description}</p>

      {/* Tags */}
      <div className="card-tags">
        {tags.map((tag) => (
          <span key={tag} className="card-tag" style={{ borderColor: `rgba(${color},0.3)`, color: `rgba(${color},0.9)` }}>
            {tag}
          </span>
        ))}
      </div>

      {/* Hover glow */}
      <div className="card-glow" style={{ background: `radial-gradient(circle at center, rgba(${color},0.08), transparent 70%)` }} />
    </motion.div>
  );
}

// ─── Remote Tech Card (same style as Technology Stack in developer page) ──────
function RemoteTechCard({ icon: Icon, name, description, link, color, delay = 0 }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.a
      ref={ref}
      href={link}
      target="_blank"
      rel="noreferrer"
      className="remote-tech-card"
      variants={scaleIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay }}
      whileHover={{
        y: -6,
        borderColor: `rgba(${color}, 0.5)`,
        boxShadow: `0 16px 40px rgba(${color}, 0.18)`,
      }}
    >
      <div className="remote-tech-icon" style={{ color: `rgb(${color})`, background: `rgba(${color},0.1)` }}>
        <Icon size={24} />
      </div>
      <div className="remote-tech-info">
        <h4 className="remote-tech-name">{name}</h4>
        <p className="remote-tech-desc">{description}</p>
      </div>
      <div className="remote-tech-border" style={{ background: `rgba(${color},0.6)` }} />
    </motion.a>
  );
}

// ─── HOME ─────────────────────────────────────────────────────
function Home({ currentPage = "", isAIChatOpen, openAIChat, theme, toggleTheme }) {
  const navigate = useNavigate();
  const featurRef = useRef(null);
  const projectsRef = useRef(null);
  const typedText = useTypingEffect(
    ["Full Stack Developer", "React Engineer", "Node.js Expert", "DevOps Engineer"],
    85, 45, 2200
  );
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, -100]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.3]);

  const handleWebRemoteControlRoute = () => window.open("/web-remote-control", "_blank");
  const handleDeveloperRoute = () => navigate("/developer");
  const handleRemoteDesktopRoute = () => {
    const top = featurRef.current.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top, behavior: "smooth" });
  };
  const handleViewProjectsRoute = () => {
    const top = projectsRef.current.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top, behavior: "smooth" });
  };
  const handleApiTesterOpen = () => {
    navigate("/api-tester")
  };
  const handleMcpBlogOpen = () => {
    navigate("/mcp-blog");
  };

  const projects = [
    {
      icon: FiMonitor,
      title: "Remote Desktop",
      description: "Desktop app using Electron & WebRTC for real-time screen sharing with low-latency peer-to-peer control.",
      tags: ["Electron", "WebRTC", "Node.js", "RobotJS"],
      color: "99, 102, 241",
      onClick: handleRemoteDesktopRoute,
    },
    {
      icon: FiGlobe,
      title: "Web Remote Control",
      description: "Browser-based remote control software. Control any PC from anywhere via the internet using Socket.IO.",
      tags: ["React", "Socket.IO", "WebRTC", "Express"],
      color: "6, 182, 212",
      onClick: handleWebRemoteControlRoute,
    },
    {
      icon: FiTerminal,
      title: "API Tester",
      description: "A powerful in-app REST API testing tool similar to Postman. Test endpoints directly in the browser.",
      tags: ["React", "REST API", "HTTP", "JSON"],
      color: "16, 185, 129",
      onClick: handleApiTesterOpen,
    },
    {
      icon: FiTool,
      title: "MCP Blog Tools",
      description: "Model Context Protocol server exposing blog post and comment workflows as structured tools, resources, and prompts.",
      tags: ["MCP", "Express", "Zod", "Streamable HTTP"],
      color: "59, 130, 246",
      onClick: handleMcpBlogOpen,
    },
    {
      icon: FiMessageSquare,
      title: "AI Chat",
      description: "Integrated AI-powered chat assistant with real-time messaging, contextual responses, and smart suggestions.",
      tags: ["AI", "React", "WebSocket", "NLP"],
      color: "245, 158, 11",
      onClick: !isAIChatOpen ? openAIChat : undefined,
    },
  ];

  // Tech stack with full descriptions — same style as Technology Stack in developer page
  const techStack = [
    { icon: SiElectron, name: "Electron", description: "Build cross-platform desktop apps with web technologies.", link: "https://www.electronjs.org/", color: "47,213,255" },
    { icon: SiWebrtc, name: "WebRTC", description: "Real-time peer-to-peer communication in web browsers.", link: "https://webrtc.org/", color: "99,236,121" },
    { icon: SiNodedotjs, name: "Node.js", description: "Cross-platform JavaScript runtime environment.", link: "https://nodejs.org/en", color: "104,194,85" },
    { icon: SiReact, name: "React", description: "Front-end library for building dynamic UI components.", link: "https://react.dev/", color: "97,218,251" },
    { icon: SiSocketdotio, name: "Socket.IO", description: "Low-latency, bidirectional event-based communication.", link: "https://socket.io/", color: "200,200,200" },
    { icon: SiExpress, name: "Express", description: "Fast, minimalist web framework for Node.js.", link: "https://expressjs.com/", color: "220,220,220" },
  ];

  const mcpToolGroups = [
    {
      title: "Post tools",
      icon: FiTool,
      items: ["create_post", "list_posts", "get_post", "update_post", "delete_post"],
    },
    {
      title: "Comment tools",
      icon: FiMessageSquare,
      items: ["add_comment", "list_comments"],
    },
    {
      title: "Context layer",
      icon: FiBookOpen,
      items: ["posts-api-guide resource", "national-duty-post-workflow prompt"],
    },
  ];

  const { ref: heroRef, inView: heroInView } = useInView({ triggerOnce: true, threshold: 0.2 });
  const { ref: statsRef, inView: statsInView } = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <>
      <Background />
      <Navbar
        currentPage={currentPage}
        isAIChatOpen={isAIChatOpen}
        openAIChat={openAIChat}
        theme={theme}
        toggleTheme={toggleTheme}
      />

      <section className="hero-section">
        <motion.div
          className="hero-content"
          ref={heroRef}
          style={{ y: heroY, opacity: heroOpacity }}
        >
          {/* Badge */}
          <motion.div
            className="hero-badge"
            variants={fadeInUp}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
          >
            <FiZap size={12} />
            <span>Available for opportunities</span>
            <motion.span
              className="hero-badge-dot"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </motion.div>

          {/* Greeting */}
          <motion.p
            className="hero-greeting"
            variants={fadeInUp}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            transition={{ delay: 0.1 }}
          >
            Hi there, I'm
          </motion.p>

          {/* Name */}
          <motion.h1
            className="hero-name"
            variants={fadeInUp}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            transition={{ delay: 0.2 }}
          >
            <span className="name-gradient">Shabin SV</span>
          </motion.h1>

          {/* Typed role */}
          <motion.div
            className="hero-role"
            variants={fadeInUp}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            transition={{ delay: 0.3 }}
          >
            <span className="role-prefix">I'm a </span>
            <span className="role-typed">{typedText}</span>
            <span className="role-cursor">|</span>
          </motion.div>

          {/* Description */}
          <motion.p
            className="hero-desc"
            variants={fadeInUp}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            transition={{ delay: 0.4 }}
          >
            Dedicated software developer passionate about turning ideas into innovative,
            scalable solutions. Skilled in JavaScript, React, Node.js, Docker & Kubernetes.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            className="hero-buttons"
            variants={fadeInUp}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            transition={{ delay: 0.5 }}
          >
            <motion.button
              className="btn-primary-hero"
              onClick={handleViewProjectsRoute}
              whileHover={{ scale: 1.04, boxShadow: "0 0 40px rgba(99,102,241,0.6)" }}
              whileTap={{ scale: 0.97 }}
            >
              <FiCode size={16} />
              View Projects
              <span className="btn-shine" />
            </motion.button>

            <motion.button
              className="btn-secondary-hero"
              onClick={handleDeveloperRoute}
              whileHover={{ scale: 1.04, borderColor: "rgba(167,139,250,0.8)", boxShadow: "0 0 25px rgba(167,139,250,0.2)" }}
              whileTap={{ scale: 0.97 }}
            >
              About Me
              <FiArrowRight size={15} />
            </motion.button>
          </motion.div>

          {/* Social links */}
          <motion.div
            className="hero-socials"
            variants={fadeInUp}
            initial="hidden"
            animate={heroInView ? "visible" : "hidden"}
            transition={{ delay: 0.6 }}
          >
            <motion.a
              href="https://github.com/shabinsv"
              target="_blank"
              rel="noreferrer"
              className="hero-social-link"
              whileHover={{ scale: 1.15, y: -3 }}
            >
              <FaGithub /> GitHub
            </motion.a>
            <span className="social-divider" />
            <motion.a
              href="https://in.linkedin.com/in/shabin-sv-831872228"
              target="_blank"
              rel="noreferrer"
              className="hero-social-link"
              whileHover={{ scale: 1.15, y: -3 }}
            >
              <FaLinkedin /> LinkedIn
            </motion.a>
            <span className="social-divider" />
            <motion.a
              href="mailto:shabinsv7@gmail.com"
              className="hero-social-link"
              whileHover={{ scale: 1.15, y: -3 }}
            >
              shabinsv7@gmail.com
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Floating code card decoration */}
        <motion.div
          className="hero-code-card"
          initial={{ opacity: 0, x: 80, rotate: 5 }}
          animate={{ opacity: 1, x: 0, rotate: 2 }}
          transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
          whileHover={{ rotate: 0, scale: 1.02 }}
        >
          <div className="code-card-header">
            <span className="code-dot red" />
            <span className="code-dot yellow" />
            <span className="code-dot green" />
            <span className="code-card-title">developer.js</span>
          </div>
          <pre className="code-card-body">{`const developer = {
  name: "Shabin SV",
  role: "Full Stack Dev",
  skills: [
    "React", "Node.js",
    "Docker", "K8s"
  ],
  passion: "Building 🚀"
};`}</pre>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="scroll-indicator"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="scroll-mouse">
            <motion.div
              className="scroll-wheel"
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </motion.div>
      </section>

      {/* ═══════════════════════ STATS ═══════════════════════ */}
      <motion.section
        className="stats-section"
        ref={statsRef}
        variants={staggerContainer}
        initial="hidden"
        animate={statsInView ? "visible" : "hidden"}
      >
        {[
          { value: 3, suffix: "+", label: "Years Experience" },
          { value: 10, suffix: "+", label: "Projects Built" },
          { value: 15, suffix: "+", label: "Technologies" },
          { value: 100, suffix: "%", label: "Passion" },
        ].map((stat, i) => (
          <motion.div key={i} className="stat-item" variants={scaleIn}>
            <div className="stat-number">
              <AnimatedCounter target={stat.value} />
              <span className="stat-suffix">{stat.suffix}</span>
            </div>
            <div className="stat-label">{stat.label}</div>
          </motion.div>
        ))}
      </motion.section>

      {/* ═══════════════════════ PROJECTS ═══════════════════════ */}
      <section className="projects-section" id="projects" ref={projectsRef}>
        <motion.div
          className="section-header-premium"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className="section-label"><FiStar size={12} /> Featured Projects</span>
          <h2 className="section-title-premium">What I've <span className="title-gradient">Built</span></h2>
          <p className="section-subtitle">A showcase of my most impactful projects — from desktop apps to cloud-native tools</p>
        </motion.div>

        <div className="projects-grid">
          {projects.map((proj, i) => (
            <ProjectCard key={proj.title} {...proj} delay={i * 0.1} />
          ))}
        </div>
      </section>

      {/* ═══════════════════════ AI WORKFLOW ═══════════════════════ */}
      <section className="mcp-showcase-section">
        <motion.div
          className="section-header-premium"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className="section-label"><FiTool size={12} /> MCP Development</span>
          <h2 className="section-title-premium">Building with <span className="title-gradient">MCP Tools</span></h2>
          <p className="section-subtitle">A streamable HTTP MCP server that turns the Posts API into reusable tools, guided workflows, and structured context.</p>
        </motion.div>

        <div className="mcp-showcase-grid">
          <motion.div
            className="mcp-showcase-card mcp-showcase-feature"
            variants={fadeInLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="section-label">What it does</span>
            <h3 className="mcp-showcase-title">Blog operations exposed as MCP capabilities</h3>
            <p className="mcp-showcase-copy">
              I built an Express-based MCP server with Streamable HTTP transport that validates
              input with Zod, connects to the Posts API, and lets clients create, read, update,
              delete, and comment on blog content through tool calls.
            </p>

            <div className="mcp-showcase-pills">
              {["Express", "McpServer", "StreamableHTTP", "Zod Validation", "CORS", "Posts API"].map((item) => (
                <span key={item} className="mcp-showcase-pill">{item}</span>
              ))}
            </div>

            <motion.button
              className="btn-primary-hero mcp-showcase-btn"
              onClick={handleMcpBlogOpen}
              whileHover={{ scale: 1.03, boxShadow: "0 0 32px rgba(59,130,246,0.35)" }}
              whileTap={{ scale: 0.97 }}
            >
              <FiTool size={16} />
              Open MCP Blog
            </motion.button>
          </motion.div>

          <motion.div
            className="mcp-showcase-card"
            variants={fadeInRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="mcp-tool-groups">
              {mcpToolGroups.map((group, index) => (
                <motion.div
                  key={group.title}
                  className="mcp-tool-group"
                  variants={fadeInUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                >
                  <div className="mcp-tool-group-head">
                    <div className="mcp-tool-icon">
                      <group.icon size={18} />
                    </div>
                    <h4>{group.title}</h4>
                  </div>
                  <div className="mcp-tool-list">
                    {group.items.map((item) => (
                      <span key={item} className="mcp-tool-chip">{item}</span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="ai-workflow-section">
        <motion.div
          className="section-header-premium"
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <span className="section-label"><FiZap size={12} /> AI-Powered Development</span>
          <h2 className="section-title-premium">Integrating <span className="title-gradient">AI</span> into My Workflow</h2>
          <p className="section-subtitle">Leveraging artificial intelligence to enhance productivity, code quality, and development speed</p>
        </motion.div>

        <div className="ai-workflow-content">
          <motion.div
            className="ai-workflow-text"
            variants={fadeInLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="ai-workflow-bullets">
              <motion.div
                className="ai-bullet"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="ai-bullet-icon">⚡</span>
                <div>
                  <strong>Code Generation & Optimization</strong>
                  <p>Utilizing AI to generate boilerplate code, optimize algorithms, and implement best practices automatically.</p>
                </div>
              </motion.div>
              
              <motion.div
                className="ai-bullet"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="ai-bullet-icon">🐛</span>
                <div>
                  <strong>Intelligent Debugging</strong>
                  <p>AI-powered error analysis and debugging assistance that identifies root causes and suggests optimal solutions.</p>
                </div>
              </motion.div>
              
              <motion.div
                className="ai-bullet"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <span className="ai-bullet-icon">🔍</span>
                <div>
                  <strong>Research & Learning</strong>
                  <p>Accelerating technology research, staying current with best practices, and rapidly learning new frameworks.</p>
                </div>
              </motion.div>
            </div>
          </motion.div>
          
          <motion.div
            className="ai-workflow-visual"
            variants={fadeInRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className="ai-visual-card">
              <div className="ai-visual-header">
                <span className="ai-visual-dot green" />
                <span className="ai-visual-dot yellow" />
                <span className="ai-visual-dot red" />
                <span className="ai-visual-title">AI Integration</span>
              </div>
              <div className="ai-visual-content">
                <div className="ai-metric">
                  <span className="ai-metric-label">Development Speed</span>
                  <span className="ai-metric-value">+40%</span>
                </div>
                <div className="ai-metric">
                  <span className="ai-metric-label">Code Quality</span>
                  <span className="ai-metric-value">+35%</span>
                </div>
                <div className="ai-metric">
                  <span className="ai-metric-label">Problem Solving</span>
                  <span className="ai-metric-value">+50%</span>
                </div>
              </div>
              <div className="ai-visual-glow" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════ REMOTE DESKTOP DETAILS ═══════════════════════ */}
      <main ref={featurRef}>

        {/* Download section */}
        <section className="download-section">
          <motion.div
            className="section-header-premium"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="section-label">Remote Desktop App</span>
            <h2 className="section-title-premium">Download & <span className="title-gradient">Install</span></h2>
          </motion.div>

          <div className="download-grid">
            {[
              {
                icon: "⊞",
                label: "Windows",
                desc: "Recommended: Windows 10 or later for best experience.",
                href: windowsFile,
                file: "windows.7z",
                color: "0, 120, 212",
              },
              {
                icon: null,
                IconComponent: SiUbuntu,
                label: "Ubuntu",
                desc: "Run the provided setup file after extracting the archive.",
                href: ubuntuFile,
                file: "linux.7z",
                color: "228, 80, 18",
              },
              {
                icon: "🌐",
                label: "Web Browser",
                desc: "Use Chrome or Edge. Firefox is not supported.",
                onClick: handleWebRemoteControlRoute,
                color: "6, 182, 212",
              },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                className="download-card"
                variants={fadeInUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                whileHover={{ y: -6, boxShadow: `0 20px 50px rgba(${item.color},0.2)` }}
              >
                <div className="download-icon" style={{ background: `rgba(${item.color},0.12)` }}>
                  {item.IconComponent
                    ? <item.IconComponent size={32} color={`rgb(${item.color})`} />
                    : <span style={{ fontSize: "1.8rem" }}>{item.icon}</span>
                  }
                </div>
                <h3 className="download-title">{item.label}</h3>
                <p className="download-desc">{item.desc}</p>
                {item.href ? (
                  <a href={item.href} download={item.file} className="download-btn" style={{ background: `rgba(${item.color},0.15)`, borderColor: `rgba(${item.color},0.3)`, color: `rgb(${item.color})` }}>
                    <FiDownload size={14} /> Download
                  </a>
                ) : (
                  <button className="download-btn" onClick={item.onClick} style={{ background: `rgba(${item.color},0.15)`, borderColor: `rgba(${item.color},0.3)`, color: `rgb(${item.color})` }}>
                    <FiGlobe size={14} /> Open Web App
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        </section>

        {/* Feature Detail 1 */}
        <section className="feature-detail-section">
          <div className="feature-detail-grid">
            <motion.div
              className="feature-img-wrap"
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <img src={desktopappImage} alt="Remote Desktop App" className="feature-img" />
              <div className="feature-img-glow" />
            </motion.div>
            <motion.div
              className="feature-text"
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <span className="section-label">Electron + WebRTC</span>
              <h2 className="feature-title">Remote Desktop <span className="title-gradient">App</span></h2>
              <div className="feature-list">
                {[
                  { icon: "⚡", title: "Control Bar", desc: "Pause streams and manage remote access with clear indicators." },
                  { icon: "🖥", title: "Full Screen Support", desc: "Remote access for entire screen. Individual window access coming soon." },
                  { icon: "🔐", title: "Secure Auth", desc: "Authorize before initiating — events sent securely to the client PC." },
                ].map((f) => (
                  <motion.div
                    key={f.title}
                    className="feature-list-item"
                    whileHover={{ x: 6 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span className="feature-item-icon">{f.icon}</span>
                    <div>
                      <strong>{f.title}</strong>
                      <p>{f.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Feature Detail 2 */}
        <section className="feature-detail-section">
          <div className="feature-detail-grid reverse">
            <motion.div
              className="feature-text"
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <span className="section-label">Upcoming Features</span>
              <h2 className="feature-title">Next <span className="title-gradient">Version</span></h2>
              <div className="feature-list">
                {[
                  { icon: "💬", title: "P2P Chat", desc: "Direct peer-to-peer messaging between two connected PCs." },
                  { icon: "📁", title: "File Sharing", desc: "Secure file transfers via TCP protocol for enhanced connectivity." },
                  { icon: "🚀", title: "Faster Access", desc: "Peer-to-peer event communication for enhanced remote access speed." },
                ].map((f) => (
                  <motion.div
                    key={f.title}
                    className="feature-list-item"
                    whileHover={{ x: 6 }}
                    transition={{ duration: 0.2 }}
                  >
                    <span className="feature-item-icon">{f.icon}</span>
                    <div>
                      <strong>{f.title}</strong>
                      <p>{f.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
            <motion.div
              className="feature-img-wrap"
              variants={fadeInRight}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <img src={desktopImage} alt="Upcoming Remote Desktop Features" className="feature-img" />
              <div className="feature-img-glow" />
            </motion.div>
          </div>
        </section>

        {/* Tech Stack — Same style as Technology Stack in developer page */}
        <section className="techstack-section">
          <motion.div
            className="section-header-premium"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="section-label">Technologies</span>
            <h2 className="section-title-premium">Tech Used for <span className="title-gradient">Remote Desktop</span></h2>
            <p className="section-subtitle">Core technologies powering the remote desktop experience</p>
          </motion.div>

          <div className="remote-tech-cards-grid">
            {techStack.map((tech, i) => (
              <RemoteTechCard key={tech.name} {...tech} delay={i * 0.08} />
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="contact-section">
          <motion.div
            className="section-header-premium"
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="section-label">Get In Touch</span>
            <h2 className="section-title-premium">Let's <span className="title-gradient">Connect</span></h2>
          </motion.div>

          <div className="contact-grid">
            <motion.div
              className="contact-map"
              variants={fadeInLeft}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <iframe
                title="google_map_trivandrum"
                src="https://maps.google.com/maps?q=trivandrum&t=&z=10&ie=UTF8&iwloc=&output=embed"
                frameBorder="0"
                style={{ border: 0, width: "100%", height: "100%", borderRadius: "16px" }}
                allowFullScreen
              />
            </motion.div>

            <motion.div
              className="contact-info-cards"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {[
                { icon: "📞", label: "Phone", value: "+91 77736107770", href: "callto:+9177736107770" },
                { icon: "📍", label: "Location", value: "Trivandrum, Kerala", href: null },
                { icon: "✉️", label: "Email", value: "shabinsv7@gmail.com", href: "mailto:shabinsv7@gmail.com" },
              ].map((item) => (
                <motion.div key={item.label} className="contact-card" variants={fadeInRight} whileHover={{ x: 6 }}>
                  <span className="contact-card-icon">{item.icon}</span>
                  <div>
                    <div className="contact-card-label">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} className="contact-card-value">{item.value}</a>
                    ) : (
                      <div className="contact-card-value">{item.value}</div>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Home;
